class MainProductSingleSubscription extends HTMLElement {
  constructor() {
    super();
    this.cart = document.querySelector("cart-drawer");
    this.productOptions = this.querySelector("product-options");
    this.flushDialog = document.querySelector("#how-many-flush-dialog");
    this.productData = JSON.parse(
      document.getElementById("main-product-single-subscription-data").textContent
    );

    // GWP (Gift With Purchase) selection state
    this.selectedGwpVariantId = null;
    this.selectedGwpTitle = null;
    this.selectedGwpCampaign = null;
    this.includedGwpInLastRequest = false;

    this.boundSummaryEventHandler = this.summaryEventHandler.bind(this);
    this.boundAddToCart = this.addToCart.bind(this);
    this.boundProductOptionChangedEventHandler =
      this.productOptionChangedEventHandler.bind(this);
    this.boundAutoDiscountUpdatedHandler =
      this.autoDiscountUpdatedHandler.bind(this);
  }

  /**
   * Lifecycle method called when the custom element is added to the DOM.
   * Sets up event listeners for subscription selectors and the "Add to Cart" button.
   */
  connectedCallback() {
    this.subscriptionSelectors = this.querySelector(
      "#main-product-single-subscription-container"
    );
    this.addToCartBtn = this.querySelector("#single-product-subscription-atc");
    this.cartStatus = this.querySelector("[data-buybox-cart-status]");
    this.savingsTag = this.querySelector("#savings-tag");
    this.discountCodeTag = this.querySelector("#discount-code-tag");
    this.discountCodeTagLabel = this.querySelector("[data-discount-code-label]");
    this.firstSubscriptionPaymentNotes = this.querySelectorAll(
      "[data-first-subscription-payment]"
    );
    this.openHowManyFlushDialogBtn = this.querySelector("#how-many-flush");

    // Register the state-update listener BEFORE reading config so we don't
    // miss an event that fires while connectedCallback is running. Recover
    // any state emitted before this element upgraded via getLastEmittedConfig.
    window.addEventListener(
      this.getAutoDiscountUpdatedEventName(),
      this.boundAutoDiscountUpdatedHandler
    );
    this.autoDiscountConfig =
      window.UqoraAutoDiscount?.getLastEmittedConfig?.() ||
      this.getAutoDiscountConfig();

    this.updateAutoDiscountUI();
    this.applyAutoDiscountCode();

    this.subscriptionSelectors.addEventListener("click", this.boundSummaryEventHandler);

    this.addToCartBtn.addEventListener("click", this.boundAddToCart);

    this.productOptions?.addEventListener(
      "click",
      this.boundProductOptionChangedEventHandler
    );

    this.openHowManyFlushDialogBtn?.addEventListener("click", () => {
      this.flushDialog.show();
    });

    // GWP event listeners (store bound handlers for cleanup)
    this._boundGwpSelectedHandler = (e) => {
      this.selectedGwpVariantId = e.detail.variantId;
      this.selectedGwpTitle = e.detail.title;
      this.selectedGwpCampaign = e.detail.campaign || null;
    };
    this._boundGwpDeselectedHandler = () => {
      this.selectedGwpVariantId = null;
      this.selectedGwpTitle = null;
      this.selectedGwpCampaign = null;
    };
    document.addEventListener("gwp:selected", this._boundGwpSelectedHandler);
    document.addEventListener("gwp:deselected", this._boundGwpDeselectedHandler);

    // Hydrate the purchase type before the first render so copied links and
    // reloads resolve to the same visible controls, variant, and cart payload.
    this.selectPurchaseType(this.getSubscriptionFromURLParam());

    // Initial load of the variant
    const selectedOptions = this.getSelectedOptions();
    this.loadVariant(selectedOptions);
  }

  /**
   * Lifecycle method called when the custom element is removed from the DOM.
   * Cleans up event listeners to avoid memory leaks.
   */
  disconnectedCallback() {
    this.subscriptionSelectors.removeEventListener(
      "click",
      this.boundSummaryEventHandler
    );
    this.addToCartBtn.removeEventListener("click", this.boundAddToCart);
    this.productOptions?.removeEventListener(
      "click",
      this.boundProductOptionChangedEventHandler
    );

    window.removeEventListener(
      this.getAutoDiscountUpdatedEventName(),
      this.boundAutoDiscountUpdatedHandler
    );

    // Clean up GWP event listeners
    document.removeEventListener("gwp:selected", this._boundGwpSelectedHandler);
    document.removeEventListener("gwp:deselected", this._boundGwpDeselectedHandler);
  }

  getAutoDiscountUpdatedEventName() {
    return (
      window.UqoraAutoDiscount?.events?.stateUpdated ||
      "uqora:auto-discount-updated"
    );
  }

  getAutoDiscountConfig() {
    if (!window.UqoraAutoDiscount) {
      return {
        enabled: false,
        code: "",
        percentage: 0,
      };
    }

    return window.UqoraAutoDiscount.getConfig();
  }

  getDiscountedPrice(price) {
    if (!window.UqoraAutoDiscount) {
      return this.roundPrice(price);
    }

    return window.UqoraAutoDiscount.applyDiscountToPrice(
      price,
      this.autoDiscountConfig
    );
  }

  roundPrice(value) {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return 0;

    return Math.round((numericValue + Number.EPSILON) * 100) / 100;
  }

  formatPrice(value) {
    const roundedValue = this.roundPrice(value);
    return Number.isInteger(roundedValue)
      ? roundedValue.toFixed(0)
      : roundedValue.toFixed(2);
  }

  applyAutoDiscountCode() {
    if (!this.autoDiscountConfig.enabled || !window.UqoraAutoDiscount) return;

    window.UqoraAutoDiscount.applyDiscountCode(this.autoDiscountConfig.code);
  }

  updateAutoDiscountUI() {
    const isEnabled = Boolean(this.autoDiscountConfig.enabled);

    if (this.discountCodeTag) {
      this.discountCodeTag.hidden = !isEnabled;
      if (this.discountCodeTagLabel) {
        this.discountCodeTagLabel.textContent =
          this.autoDiscountConfig.code || "REGIMEN";
      }
    }

    // When disabled, keep notes hidden. When enabled, updatePrices controls
    // per-note visibility based on the per-template subscription_note_type.
    if (!isEnabled) {
      this.firstSubscriptionPaymentNotes?.forEach((note) => {
        note.hidden = true;
      });
    }
  }

  autoDiscountUpdatedHandler(event) {
    const nextConfig = event.detail || this.getAutoDiscountConfig();
    this.autoDiscountConfig = {
      enabled: Boolean(nextConfig.enabled),
      code: nextConfig.code || "",
      percentage: Number(nextConfig.percentage) || 0,
    };

    this.updateAutoDiscountUI();
    this.applyAutoDiscountCode();

    const selectedOptions = this.getSelectedOptions();
    this.loadVariant(selectedOptions);
  }

  /**
   * Event handler for subscription option toggling.
   * Updates the open state of subscription details and marks the selected option.
   * @param {Event} event - The click event triggered on the subscription selectors.
   */
  summaryEventHandler(event) {
    event.preventDefault();
    const currentSummary = event.target.closest("summary");
    if (!currentSummary) return;

    // Close all details and reset their radio button states
    this.subscriptionSelectors.querySelectorAll("details").forEach((el) => {
      el.removeAttribute("open");
      el.querySelector(".subscription-radio-button").setAttribute(
        "data-subscription-radio-button-checked",
        false
      );
    });

    // Mark the selected subscription option
    currentSummary
      .querySelector(".subscription-radio-button")
      .setAttribute("data-subscription-radio-button-checked", true);

    // Open the relevant details
    currentSummary.parentElement.open = true;

    // Update savings display on switch
    const selectedOptions = this.getSelectedOptions();
    const [variant] = this.productData.variants.filter(
      (el) => el.handle === selectedOptions
    );

    if (!variant) return;

    this.updateSavingsTag(variant, this.currentPurchaseType);
    this.dispatchPriceUpdateEvent(variant);
    this.updateURLWithVariantAndSubscription(
      variant.id,
      this.getCurrentSubscriptionType()
    );
  }

  /**
   * Event handler for product options
   */
  productOptionChangedEventHandler(e) {
    const btn = e.target.closest("button");
    if (!btn || !btn.getAttribute("data-option-handle")) return;
    const selectedOptions = this.getSelectedOptions();
    this.loadVariant(selectedOptions);
  }

  /**
   * Search for the variant object based on the handle
   * Then updates the content
   * @param {String} variantHandle Selected variant handle
   */
  loadVariant(variantHandle) {
    const [variant] = this.productData.variants.filter(
      (el) => el.handle === variantHandle
    );

    if (!variant) return;

    this.updatePrices(variant);
    this.updateSummariesId(variant);
    this.updateSavingsTag(variant, this.currentPurchaseType);
    this.dispatchPriceUpdateEvent(variant);
    this.updateURLWithVariantAndSubscription(
      variant.id,
      this.getCurrentSubscriptionType()
    );
  }

  dispatchPriceUpdateEvent(variant) {
    const discountedVariant = {
      ...variant,
      price: this.getDiscountedPrice(variant.price),
      sellingPlanPrice: this.getDiscountedPrice(variant.sellingPlanPrice),
    };

    const updateEvent = new CustomEvent(
      "main-product-single-subscription::update-prices",
      {
        detail: {
          variant: discountedVariant,
          subscriptionSelected: this.currentPurchaseType,
          autoDiscount: this.autoDiscountConfig,
        },
      }
    );
    document.dispatchEvent(updateEvent);
  }

  /**
   * Updates the prices based on the selected variant
   * @param {Object} variant
   */
  updatePrices(variant) {
    const { price, sellingPlanPrice, comparePrice } = variant;

    const discountedPrice = this.getDiscountedPrice(price);
    const discountedSellingPlanPrice = this.getDiscountedPrice(sellingPlanPrice);

    this.subscriptionSelectors.querySelector(
      "#current-selling-plan-price"
    ).innerHTML = `$${this.formatPrice(discountedSellingPlanPrice)}${this.autoDiscountConfig.enabled ? '' : ' /mo'}`;

    this.subscriptionSelectors
      .querySelectorAll(".current-variant-price")
      .forEach(
        (el) =>
          (el.innerHTML =
            comparePrice === 0 ? "" : `$${this.formatPrice(comparePrice)}`)
      );

    // Update the prices in the one-time purchase option
    // When auto-discount is active, show the original price as strikethrough.
    // Otherwise fall back to Shopify's compare-at price when it differs from price.
    let oneTimeComparePrice = "";
    if (this.autoDiscountConfig.enabled && variant.strikethroughPrice != null) {
      oneTimeComparePrice = `$${this.formatPrice(variant.strikethroughPrice)}`;
    } else if (comparePrice !== 0 && comparePrice !== price) {
      oneTimeComparePrice = `$${this.formatPrice(comparePrice)}`;
    }
    this.querySelector("#one-time-compare-at-price").innerHTML = oneTimeComparePrice;

    this.querySelector(
      "#one-time-variant-price"
    ).innerHTML = `$${this.formatPrice(discountedPrice)}`;

    // Auto-discount first-payment note. Format chosen per-template via the
    // single_subscription block setting `subscription_note_type` (see
    // sections/main-product.liquid schema). Only rendered when the V2
    // Auto-apply variant is active.
    this.firstSubscriptionPaymentNotes?.forEach((note) => {
      const noteType = this.productData.subscriptionNoteType || 'monthly';
      let text = null;
      if (this.autoDiscountConfig.enabled && sellingPlanPrice > 0) {
        if (noteType === 'monthly') {
          text = `$${this.formatPrice(sellingPlanPrice)}/mo after first payment`;
        } else if (noteType === 'per_unit') {
          text = `$${this.formatPrice(sellingPlanPrice)} after first payment`;
        }
      }
      if (text) {
        note.innerHTML = text;
        note.hidden = false;
      } else {
        note.hidden = true;
      }
    });
  }

  /**
   * Updates the summaries variant id
   * @param {Object} variant
   */
  updateSummariesId(variant) {
    this.subscriptionSelectors
      .querySelectorAll(".subscription-radio-button")
      .forEach((el) => (el.dataset.currentVariantId = variant.id));
  }

  /**
   * Updates the savings percentage based on the variant's price and purchase type
   * @param {Object} variant
   * @param {String} purchaseType - "one-time" or "subscription"
   */
  updateSavingsTag(variant, purchaseType = "subscription") {
    const { price, comparePrice, sellingPlanPrice } = variant;
    const discountedPrice = this.getDiscountedPrice(price);
    const discountedSellingPlanPrice = this.getDiscountedPrice(sellingPlanPrice);

    // Get the setting for whether to show savings tag on one-time purchase
    const showOnOneTime =
      this.savingsTag.getAttribute("data-show-savings-one-time-purchase") ===
      "true";

    // Determine if we should show the tag
    if (purchaseType === "one-time" && !showOnOneTime) {
      this.savingsTag.style.display = "none";
      return;
    }

    // Determine base price for savings calculation
    const basePrice = comparePrice > 0 ? comparePrice : price;

    let savings = 0;
    if (purchaseType === "subscription") {
      savings = Math.round(
        ((basePrice - discountedSellingPlanPrice) / basePrice) * 100
      );
    } else {
      savings =
        comparePrice > 0
          ? Math.round(((comparePrice - discountedPrice) / comparePrice) * 100)
          : 0;
    }

    if (savings <= 0) {
      this.savingsTag.style.display = "none";
      return;
    }

    this.savingsTag.style.display = "block";
    if (purchaseType === "subscription") {
      this.savingsTag.innerHTML = `Save ${savings}%`;
      this.savingsTag.classList.remove("one-time-purchase-active");
    } else {
      this.savingsTag.innerHTML = `Save ${savings}% vs Regular Price`;
      this.savingsTag.classList.add("one-time-purchase-active");
    }
  }

  /**
   * Builds the variant handle based on the selected options
   * @returns {String} variant handle
   */
  getSelectedOptions() {
    return Array.from(
      this.productOptions.querySelectorAll(`button[data-variant-selected="true"]`)
    )
      .map((el) => {
        return el.dataset.optionHandle;
      })
      .join("-");
  }

  /**
   * Getter for current purchase type ("subscription" or "one-time")
   */
  get currentPurchaseType() {
    const details = this.subscriptionSelectors.querySelector("details[open]");
    if (!details) return "subscription";

    return details === this.subscriptionSelectors.querySelectorAll("details")[1]
      ? "one-time"
      : "subscription";
  }

  /**
   * Returns the legacy URL value for the currently visible purchase type.
   * @returns {"default-selling-plan"|"one-time-purchase"}
   */
  getCurrentSubscriptionType() {
    return this.currentPurchaseType === "one-time"
      ? "one-time-purchase"
      : "default-selling-plan";
  }

  /**
   * Reads a supported legacy purchase type from the URL. Missing and stale
   * values intentionally fall back to the existing subscription default.
   * @returns {"default-selling-plan"|"one-time-purchase"}
   */
  getSubscriptionFromURLParam() {
    try {
      const subscriptionType = new URL(window.location.href).searchParams.get(
        "subscription"
      );
      return subscriptionType === "one-time-purchase"
        ? "one-time-purchase"
        : "default-selling-plan";
    } catch (_) {
      return "default-selling-plan";
    }
  }

  /**
   * Keeps the open details panel and its custom radio indicator in sync.
   * @param {"default-selling-plan"|"one-time-purchase"} subscriptionType
   */
  selectPurchaseType(subscriptionType) {
    const details = Array.from(
      this.subscriptionSelectors.querySelectorAll("details")
    );
    if (!details.length) return;

    const selectedIndex = subscriptionType === "one-time-purchase" ? 1 : 0;
    details.forEach((detail, index) => {
      const isSelected = index === selectedIndex;
      detail.open = isSelected;
      detail
        .querySelector(".subscription-radio-button")
        ?.setAttribute(
          "data-subscription-radio-button-checked",
          String(isSelected)
        );
    });
  }

  /**
   * Updates the canonical legacy selection without disturbing experiment or
   * preview parameters already present on the URL.
   */
  updateURLWithVariantAndSubscription(variantId, subscriptionType) {
    if (!this.isLegacyBuyBoxActive()) return;

    try {
      const url = new URL(window.location.href);
      const variantMatches =
        url.searchParams.get("variant") === String(variantId);
      const subscriptionMatches =
        url.searchParams.get("subscription") === subscriptionType;
      if (variantMatches && subscriptionMatches) return;

      url.searchParams.set("variant", String(variantId));
      url.searchParams.set("subscription", subscriptionType);
      window.history.replaceState(window.history.state, "", url.toString());
    } catch (_) {
      // Leave navigation untouched when the current URL cannot be parsed.
    }
  }

  /**
   * Only the visible legacy arm may own URL state while both buyboxes are
   * mounted for the experiment.
   */
  isLegacyBuyBoxActive() {
    const managedUnifiedBuyBox = document.querySelector(
      "uq-buybox[data-uq-ab-managed]"
    );
    if (!managedUnifiedBuyBox) return true;

    const qaArm = document.body?.getAttribute("data-uq-buybox-qa");
    if (qaArm === "legacy") return true;
    if (qaArm === "new") return false;

    return !document.body?.classList.contains("vwo-new-buybox");
  }

  /**
   * Handles adding a subscription product to the cart.
   * Sends a request to Shopify's cart API and updates the cart contents.
   */
  setCartStatus(message, isError = false) {
    if (!this.cartStatus) return;
    this.cartStatus.textContent = message || "";
    this.cartStatus.dataset.status = isError ? "error" : "info";
    this.cartStatus.hidden = !message;
  }

  async addToCart() {
    if (this._addToCartPending) return;
    this._addToCartPending = true;
    const originalLabel = this.addToCartBtn.innerHTML;
    let cartCommitted = false;
    let gwpClaimed = false;
    const claimGwp = () => {
      if (!this.includedGwpInLastRequest || gwpClaimed) return;
      gwpClaimed = true;
      try {
        document.dispatchEvent(new CustomEvent("gwp:claimed"));
      } finally {
        this.selectedGwpVariantId = null;
        this.selectedGwpTitle = null;
        this.selectedGwpCampaign = null;
      }
    };

    try {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.innerHTML = "ADDING TO THE CART";
      this.setCartStatus("");

      // Wait for any in-flight discount apply/clear to land before adding the
      // item, so the cart's discount state is consistent at /cart/add.js time.
      if (window.UqoraAutoDiscount?.pendingCartUpdate) {
        try { await window.UqoraAutoDiscount.pendingCartUpdate; } catch (_) {}
      }
      if (window.UqoraCampaignOffers?.getActiveOffer?.()) {
        const campaignReady = await window.UqoraCampaignOffers.ensureCartAttribute();
        if (!campaignReady) {
          this.setCartStatus("We could not prepare this offer. Please try again.", true);
          return;
        }
      }

      const formData = this.buildFormData();
      const variantId = formData.items[0]?.id;

      const request = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (!request.ok) {
        const errorText = await request.text();
        throw new Error(`cart/add.js failed: ${request.status} ${errorText}`);
      }

      cartCommitted = true;
      let response = await request.json();

      // The item is already committed at this point. Claim GWP state exactly
      // once even if discount verification or cart-drawer rendering fails.
      claimGwp();

      let discountWarning = false;
      if (
        this.autoDiscountConfig.enabled &&
        window.UqoraAutoDiscount?.ensureDiscountCodeApplied
      ) {
        const verification = await window.UqoraAutoDiscount.ensureDiscountCodeApplied(
          this.autoDiscountConfig.code,
          {
            sections: formData.sections || [],
            sectionsUrl: formData.sections_url || window.location.pathname,
          }
        );
        if (verification.cart) response = verification.cart;
        discountWarning = !verification.ok;
      }

      let uiSyncFailed = false;
      try {
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: variantId,
            cartData: response,
          });
        }
      } catch (error) {
        uiSyncFailed = true;
        console.error("Single Subscription | cart publish failed", error);
      }

      try {
        this.cart?.renderContents?.(response);
      } catch (error) {
        uiSyncFailed = true;
        console.error("Single Subscription | cart render failed", error);
      }

      if (discountWarning) {
        this.setCartStatus(
          "Added to cart, but the promotional discount could not be applied. Review your cart.",
          true
        );
      } else if (uiSyncFailed) {
        this.setCartStatus("Added to cart. Refresh to view the updated cart.", true);
      } else {
        this.setCartStatus("Added to cart.");
      }
    } catch (error) {
      if (cartCommitted) {
        claimGwp();
        console.error("Single Subscription | post-add sync failed", error);
        this.setCartStatus("Added to cart. Refresh to view the updated cart.", true);
      } else {
        console.error("Single Subscription | addToCart failed", error);
        this.setCartStatus("We couldn't add this item to your cart. Please try again.", true);
      }
    } finally {
      this._addToCartPending = false;
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.innerHTML = originalLabel;
      if (cartCommitted && this.cart?.classList.contains("is-empty")) {
        this.cart.classList.remove("is-empty");
      }
    }
  }

  generateBundleId() {
    return `gwp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Builds the form data required for adding a subscription product to the cart.
   * If a GWP is selected or automatically eligible, it adds the GWP as a separate line item with _gwp_uqora property.
   * Both items share a _gwp_bundle_id so they can be removed together.
   * @returns {Object} The form data object containing item details and rendering information.
   */
  buildFormData() {
    const openDetails = this.subscriptionSelectors.querySelector("details[open]");
    const { sellingPlanId, currentVariantId } = openDetails.querySelector(
      ".subscription-radio-button"
    ).dataset;

    // Build items array - main product first
    const items = [
      {
        id: currentVariantId,
        quantity: 1,
        ...(sellingPlanId && {
          selling_plan: sellingPlanId,
        }),
      },
    ];

    const giftResult = window.UqoraCampaignOffers?.appendGiftLine?.({
      items,
      productHandle: this.productData.productHandle,
      selectedVariantId: this.selectedGwpVariantId,
      selectedCampaign: this.selectedGwpCampaign,
      createBundleId: () => this.generateBundleId(),
    });
    this.includedGwpInLastRequest = Boolean(giftResult?.included);

    const formData = { items };
    if (this.cart?.getSectionsToRender) {
      formData.sections = this.cart.getSectionsToRender().map((section) => section.id);
      formData.sections_url = window.location.pathname;
    }
    return formData;
  }
}

customElements.define(
  "main-product-single-subscription",
  MainProductSingleSubscription
);
